import * as THREE from "three";
import { inked, toon } from "../toon";
import { mergeFlat } from "./geom";

// The natural build's hand (#75, the realistic rider), past a box palm and
// five capsules. Same frame as the mitten: the wrist at the origin, down the
// arm is -y, the knuckles run along z, the palm faces +x (the way the
// fingers curl) and the thumb is on +z.
//
// - A palm that's narrower and thicker at the wrist, wider and thinner
//   across the knuckles, with the thumb's pad swelling out of its heel.
// - Four fingers of three bones each (a long one off the knuckle, two
//   shorter), tapering, each joint curled a little more toward the palm;
//   the middle finger longest, the little finger shortest and set back.
// - A thumb of two bones out of the pad, turned in across the palm.
// - A nail on the back of each fingertip and the thumb's.
//
// The skin is one merged mesh and the nails another: two draws (the
// mitten's three).

/** One finger: where its knuckle sits across the hand (z), how far back (y), its length and girth. */
interface FingerSpec {
  z: number;
  y: number;
  length: number;
  radius: number;
}

/** Index (next to the thumb, +z) to little finger. */
export const FINGERS: readonly FingerSpec[] = [
  { z: 0.026, y: -0.042, length: 0.05, radius: 0.0088 },
  { z: 0.009, y: -0.044, length: 0.056, radius: 0.0092 },
  { z: -0.008, y: -0.043, length: 0.052, radius: 0.0086 },
  { z: -0.023, y: -0.039, length: 0.042, radius: 0.0074 },
];

/** How long each bone is, as a share of the finger, knuckle outward. */
const BONES = [0.46, 0.3, 0.24];
/** How far each joint bends toward the palm (radians), knuckle outward. */
export const CURL = [0.22, 0.32, 0.26];
/** How thin each bone gets at its far end, as a share of the finger's radius. */
const TAPER = [1, 0.95, 0.89];

/** Palm dimensions: across (z) and thickness (x) at the wrist and at the knuckles, and its length (y). */
const PALM = { wristWidth: 0.056, knuckleWidth: 0.068, wristThick: 0.034, knuckleThick: 0.024, length: 0.05 };

/**
 * The palm: a sphere squared off toward a box (a superellipsoid, so it's
 * dense everywhere and the pad can swell smoothly), then tapered.
 */
export function palmGeometry(): THREE.BufferGeometry {
  const geo = new THREE.SphereGeometry(0.5, 18, 12);
  const pos = geo.getAttribute("position");
  const square = (c: number) => (Math.sign(c) * Math.abs(2 * c) ** 0.5) / 2;
  for (let i = 0; i < pos.count; i++) {
    const x = square(pos.getX(i));
    const y = square(pos.getY(i));
    const z = square(pos.getZ(i));
    const t = 0.5 - y; // 0 at the wrist (top), 1 at the knuckles
    const width = PALM.wristWidth + (PALM.knuckleWidth - PALM.wristWidth) * Math.min(1, t * 1.3);
    const thick = PALM.wristThick + (PALM.knuckleThick - PALM.wristThick) * t;
    // The thumb's pad: the palm side (+x) swells on the thumb side (+z), low in the heel.
    const pad = x > 0 && z > 0 ? 0.009 * Math.sin(Math.PI * Math.min(1, t * 1.3)) * Math.sqrt(z * 2) * (x * 2) : 0;
    pos.setXYZ(i, x * thick + pad, -0.004 - t * PALM.length, z * width);
  }
  geo.computeVertexNormals();
  return geo;
}

/**
 * A chain of bones from `start` down -y, each joint turning toward +x by its
 * `curl` after the tilt `lean` (about x, splaying the finger across the
 * hand). Returns each bone's geometry and the tip's position and direction.
 */
function chain(
  start: THREE.Vector3,
  lengths: number[],
  radii: number[],
  curls: number[],
  lean: number,
  turn = new THREE.Quaternion(),
): { bones: THREE.BufferGeometry[]; tip: THREE.Vector3; dir: THREE.Quaternion } {
  const at = start.clone();
  // Down the arm, splayed by `lean`, then any extra turn (the thumb's).
  const dir = turn.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), lean));
  const bend = new THREE.Quaternion();
  const bones: THREE.BufferGeometry[] = [];
  lengths.forEach((len, i) => {
    dir.multiply(bend.setFromAxisAngle(new THREE.Vector3(0, 0, 1), curls[i]!));
    const r = radii[i]!;
    const bone = new THREE.CapsuleGeometry(r, Math.max(len - r, 0.001), 3, 8);
    bone.translate(0, -len / 2, 0);
    bone.applyQuaternion(dir);
    bone.translate(at.x, at.y, at.z);
    bones.push(bone);
    at.add(new THREE.Vector3(0, -len, 0).applyQuaternion(dir));
  });
  return { bones, tip: at, dir };
}

/** A nail on the back (-x, before the bone's own turn) of a fingertip. */
function nail(tip: THREE.Vector3, dir: THREE.Quaternion, radius: number): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(1, 8, 5);
  g.scale(radius * 0.3, radius * 1.1, radius * 0.72);
  // Just short of the tip, barely proud of the skin on the back.
  g.translate(-radius * 0.82, radius * 1.5, 0);
  g.applyQuaternion(dir);
  g.translate(tip.x, tip.y, tip.z);
  return g;
}

export function buildNaturalHand(skin: THREE.MeshStandardMaterial): THREE.Group {
  const hand = new THREE.Group();
  hand.name = "hand";
  const skinParts: THREE.BufferGeometry[] = [palmGeometry()];
  const nailParts: THREE.BufferGeometry[] = [];

  for (const f of FINGERS) {
    const lengths = BONES.map((b) => b * f.length);
    const radii = TAPER.map((t) => t * f.radius);
    // The outer fingers splay a little away from the middle (a lean about x tips -y toward -z).
    const lean = -f.z * 2.4;
    const { bones, tip, dir } = chain(new THREE.Vector3(-0.002, f.y, f.z), lengths, radii, CURL, lean);
    skinParts.push(...bones);
    nailParts.push(nail(tip, dir, radii[2]!));
  }

  // The thumb: out of the pad, down alongside the index and in toward the palm.
  const turn = new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.38, 0, 0.75));
  const thumb = chain(new THREE.Vector3(0.008, -0.014, 0.025), [0.027, 0.023], [0.0105, 0.009], [0.1, 0.25], 0, turn);
  skinParts.push(...thumb.bones);
  nailParts.push(nail(thumb.tip, thumb.dir, 0.009));

  const mesh = inked(mergeFlat(skinParts), skin, 0.008);
  mesh.name = "fingers";
  hand.add(mesh);
  // A touch lighter and glossier than the skin.
  const tone = skin.color.clone().lerp(new THREE.Color("#fff1ea"), 0.45);
  const nails = new THREE.Mesh(mergeFlat(nailParts), toon(tone, { roughness: 0.45 }, "skin"));
  nails.name = "nails";
  hand.add(nails);
  return hand;
}
